import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tag, Table, Tabs, Statistic, Badge, Progress, Tooltip, Spin, Empty, Select, Button, Input, message, Divider } from 'antd';
import {
  RobotOutlined,
  ThunderboltOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CloudServerOutlined,
  FireOutlined,
  RiseOutlined,
  DashboardOutlined,
  HddOutlined,
  DatabaseOutlined,
  SettingOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import {
  useOllamaServersQuery,
  useOllamaStatusQuery,
  useOllamaStatsQuery,
  useOllamaConfigQuery,
  useUpdateOllamaConfigMutation,
  usePullOllamaModelMutation,
  useLoadOllamaModelMutation,
  useViewServerQuery,
  useLoadHistoryQuery,
  useCpuHistoryQuery,
  useRamHistoryQuery,
  useNetworkHistoryQuery,
  useDiskHistoryQuery,
} from '../store/api';

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatTokens = (n) => {
  if (!n || n === 0) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

const formatDuration = (ms) => {
  if (!ms || ms === 0) return '0ms';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export default function OllamaMonitor() {
  const [activeTab, setActiveTab] = useState('today');
  const [pullModelName, setPullModelName] = useState('');
  const [selectedServerId, setSelectedServerId] = useState(null);

  // ── Servidores IA seleccionables ─────────────────────
  const { data: serversData } = useOllamaServersQuery();
  const aiServers = serversData?.servers || [];
  const selectedServer = aiServers.find(s => s.id === selectedServerId) || aiServers[0] || null;

  useEffect(() => {
    if (selectedServerId === null && aiServers.length > 0) {
      setSelectedServerId(aiServers[0].id);
    }
  }, [aiServers, selectedServerId]);

  const { data: statusData, isLoading: statusLoading, isFetching: statusFetching } = useOllamaStatusQuery(selectedServerId, {
    pollingInterval: 10000,
  });

  const { data: statsData, isLoading: statsLoading, isFetching: statsFetching } = useOllamaStatsQuery(undefined, {
    pollingInterval: 15000,
  });

  // ── Configuración del modelo ───────────────────────
  const { data: configData, isLoading: configLoading } = useOllamaConfigQuery(undefined, {
    pollingInterval: 30000,
  });
  const [updateConfig, { isLoading: updatingConfig }] = useUpdateOllamaConfigMutation();
  const [pullModel, { isLoading: pullingModel }] = usePullOllamaModelMutation();
  const [loadModel, { isLoading: loadingModel }] = useLoadOllamaModelMutation();

  const defaultModel = configData?.config?.default_model?.value || 'qwen2.5-coder:14b';

  // ── Server hardware metrics (según el servidor IA seleccionado) ──
  const SERVER_ID = selectedServer?.estadoservidor_id || 23;
  const { data: serverData } = useViewServerQuery(SERVER_ID, { pollingInterval: 30000 });
  const { data: loadData } = useLoadHistoryQuery({ dias: 1, agrupacion: 'raw', id_servidor: SERVER_ID }, { pollingInterval: 30000 });
  const { data: cpuData } = useCpuHistoryQuery({ dias: 1, id_servidor: SERVER_ID }, { pollingInterval: 30000 });
  const { data: ramData } = useRamHistoryQuery({ dias: 1, id_servidor: SERVER_ID }, { pollingInterval: 30000 });
  const { data: netData } = useNetworkHistoryQuery({ dias: 1, id_servidor: SERVER_ID }, { pollingInterval: 30000 });
  const { data: diskIoData } = useDiskHistoryQuery({ dias: 1, id_servidor: SERVER_ID }, { pollingInterval: 30000 });

  const serverInfo = serverData?.estadoservidores || {};
  const loadSeries = loadData?.servidores?.[0]?.datos || [];
  const cpuSeries = cpuData?.servidores?.[0]?.datos || [];
  const ramSeries = ramData?.servidores?.[0]?.datos || [];
  const netSeries = netData?.servidores?.[0]?.datos || [];
  const diskIoSeries = diskIoData?.servidores?.[0]?.datos || [];

  const online = statusData?.online ?? false;
  const runningModels = statusData?.running_models || [];
  const availableModels = statusData?.available_models || [];

  const today = statsData?.today || {};
  const month = statsData?.month || {};
  const byOriginToday = statsData?.by_origin_today || [];
  const byOriginMonth = statsData?.by_origin_month || [];
  const hourlyToday = statsData?.hourly_today || [];
  const daily30d = statsData?.daily_30d || [];
  const topModels = statsData?.top_models || [];
  const recentChats = statsData?.recent_chats || [];

  // ── Hourly chart config ────────────────────────────
  const hourlyChartOptions = {
    chart: { type: 'area', toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
    xaxis: {
      categories: hourlyToday.map(h => `${String(h.hora).padStart(2, '0')}:00`),
      labels: { style: { fontSize: '11px', colors: '#94a3b8' } },
    },
    yaxis: [
      { title: { text: 'Chats', style: { color: '#845adf' } }, labels: { style: { colors: '#94a3b8' } } },
      { opposite: true, title: { text: 'Tokens', style: { color: '#23b7e5' } }, labels: { style: { colors: '#94a3b8' } } },
    ],
    colors: ['#845adf', '#23b7e5'],
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
    dataLabels: { enabled: false },
    tooltip: { theme: 'dark' },
    grid: { borderColor: '#e9edf4', strokeDashArray: 3 },
  };

  const hourlyChartSeries = [
    { name: 'Chats', data: hourlyToday.map(h => h.chats) },
    { name: 'Tokens', data: hourlyToday.map(h => h.tokens) },
  ];

  // ── Daily chart config ─────────────────────────────
  const dailyChartOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
    xaxis: {
      categories: daily30d.map(d => d.fecha?.substring(5) || ''),
      labels: { style: { fontSize: '10px', colors: '#94a3b8' }, rotate: -45 },
    },
    yaxis: [
      { title: { text: 'Chats' }, labels: { style: { colors: '#94a3b8' } } },
      { opposite: true, title: { text: 'Tokens' }, labels: { style: { colors: '#94a3b8' } } },
    ],
    colors: ['#845adf', '#f5b849'],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
    dataLabels: { enabled: false },
    tooltip: { theme: 'dark' },
    grid: { borderColor: '#e9edf4', strokeDashArray: 3 },
  };

  const dailyChartSeries = [
    { name: 'Chats', type: 'bar', data: daily30d.map(d => parseInt(d.chats) || 0) },
    { name: 'Tokens', type: 'line', data: daily30d.map(d => parseInt(d.tokens) || 0) },
  ];

  // ── Origin pie chart config ────────────────────────
  const originData = activeTab === 'today' ? byOriginToday : byOriginMonth;
  const originPieOptions = {
    chart: { type: 'donut', fontFamily: 'Inter, sans-serif' },
    labels: originData.map(o => o.origin),
    colors: ['#845adf', '#23b7e5', '#f5b849', '#e6533c', '#26bf94', '#49b6f5', '#f17171', '#8c6bef'],
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%` },
    tooltip: { theme: 'dark' },
  };
  const originPieSeries = originData.map(o => parseInt(o.chats) || 0);

  // ── Table columns for origin ───────────────────────
  const originColumns = [
    { title: 'Origen', dataIndex: 'origin', key: 'origin', render: (v) => <Tag color="purple">{v}</Tag> },
    { title: 'Chats', dataIndex: 'chats', key: 'chats', sorter: (a, b) => a.chats - b.chats, render: (v) => <strong>{parseInt(v).toLocaleString()}</strong> },
    { title: 'Tokens Entrada', dataIndex: 'prompt_tokens', key: 'prompt_tokens', render: (v) => formatTokens(parseInt(v)) },
    { title: 'Tokens Salida', dataIndex: 'completion_tokens', key: 'completion_tokens', render: (v) => formatTokens(parseInt(v)) },
    { title: 'Total Tokens', dataIndex: 'total_tokens', key: 'total_tokens', sorter: (a, b) => a.total_tokens - b.total_tokens, render: (v) => <strong>{formatTokens(parseInt(v))}</strong> },
  ];

  // ── Table columns for recent chats ─────────────────
  const recentColumns = [
    { title: 'Hora', dataIndex: 'created_at', key: 'created_at', width: 150, render: (v) => v?.substring(11, 19) || '' },
    { title: 'Modelo', dataIndex: 'model', key: 'model', render: (v) => <Tag color="blue">{v}</Tag> },
    { title: 'Origen', dataIndex: 'origin', key: 'origin', render: (v) => <Tag color="purple">{v}</Tag> },
    { title: 'In', dataIndex: 'prompt_tokens', key: 'pt', width: 80, render: (v) => formatTokens(v) },
    { title: 'Out', dataIndex: 'completion_tokens', key: 'ct', width: 80, render: (v) => formatTokens(v) },
    { title: 'Duración', dataIndex: 'duration_ms', key: 'dur', width: 100, render: (v) => formatDuration(v) },
    {
      title: 'Estado', dataIndex: 'status', key: 'status', width: 90,
      render: (v) => v === 'success'
        ? <Tag color="success" icon={<CheckCircleOutlined />}>OK</Tag>
        : <Tag color="error" icon={<CloseCircleOutlined />}>{v}</Tag>,
    },
  ];

  const isRefreshing = statusFetching || statsFetching;

  return (
    <div className="page-container fade-in">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div className="page-header-left">
          <h1 className="page-title">
            <RobotOutlined style={{ marginRight: 8, color: '#845adf' }} />
            IA SMART Monitor
          </h1>
          <div className="page-subtitle">
            {statusData?.host || 'Servidor IA'} —
            <Tag color={selectedServer?.tipo === 'openai' ? 'cyan' : 'purple'} style={{ marginLeft: 8 }}>
              {selectedServer?.tipo === 'openai' ? 'OpenAI-compatible' : 'Ollama'}
            </Tag>
            <Badge
              status={online ? 'success' : 'error'}
              text={online ? 'En línea' : 'Sin conexión'}
              style={{ marginLeft: 8, fontWeight: 600 }}
            />
            {isRefreshing && <Spin size="small" style={{ marginLeft: 12 }} />}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            style={{ minWidth: 240 }}
            value={selectedServerId}
            onChange={setSelectedServerId}
            loading={!aiServers.length}
            placeholder="Servidor IA"
            options={aiServers.map(s => ({
              label: `${s.nombre} (${s.base_url})`,
              value: s.id,
            }))}
          />
          <Tag icon={<ReloadOutlined spin={isRefreshing} />} color={isRefreshing ? 'processing' : 'default'}>
            Auto-refresh {isRefreshing ? '...' : '10s'}
          </Tag>
        </div>
      </div>

      {/* GPU / Models Status */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                <ThunderboltOutlined style={{ marginRight: 6, color: '#f5b849' }} />
                GPU & Modelos en Ejecución
              </span>
            }
          >
            {statusLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
            ) : !online ? (
              <Empty description="Servidor IA no disponible" />
            ) : runningModels.length === 0 ? (
              <Empty description={selectedServer?.tipo === 'openai' ? 'No hay modelos en ejecución (API compatible OpenAI)' : 'No hay modelos cargados en GPU'} />
            ) : (
              <Row gutter={[16, 16]}>
                {runningModels.map((m, i) => (
                  <Col xs={24} sm={12} lg={8} key={i}>
                    <Card size="small" bordered style={{ borderRadius: 10, borderLeft: '3px solid #845adf' }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: '#1e293b' }}>
                        <CloudServerOutlined style={{ marginRight: 6, color: '#845adf' }} />
                        {m.name}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ color: '#64748b', fontSize: 12 }}>VRAM Usada</span>
                        <Progress
                          percent={m.vram_pct}
                          strokeColor={{ '0%': '#845adf', '100%': '#23b7e5' }}
                          size="small"
                          format={(p) => `${p}%`}
                        />
                      </div>
                      <Row gutter={8}>
                        <Col span={12}>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Tamaño</div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{formatBytes(m.size)}</div>
                        </Col>
                        <Col span={12}>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>En VRAM</div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{formatBytes(m.size_vram)}</div>
                        </Col>
                      </Row>
                      {m.details?.family && (
                        <Tag color="geekblue" style={{ marginTop: 8 }}>{m.details.family}</Tag>
                      )}
                      {m.details?.parameter_size && (
                        <Tag color="cyan" style={{ marginTop: 8 }}>{m.details.parameter_size}</Tag>
                      )}
                      {m.details?.quantization_level && (
                        <Tag style={{ marginTop: 8 }}>{m.details.quantization_level}</Tag>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
            {online && availableModels.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid #e9edf4', paddingTop: 12 }}>
                <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                  MODELOS DISPONIBLES ({availableModels.length}):
                </span>
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {availableModels.map((m, i) => {
                    const isRunning = runningModels.some(r => r.name === m.name);
                    return (
                      <Tooltip title={`${formatBytes(m.size)}${m.details?.parameter_size ? ` — ${m.details.parameter_size}` : ''}`} key={i}>
                        <Tag color={isRunning ? 'success' : 'default'} style={{ cursor: 'default' }}>
                          {isRunning && <FireOutlined style={{ marginRight: 4 }} />}
                          {m.name}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Model Configuration */}
      {selectedServer?.tipo !== 'openai' && (
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                <SettingOutlined style={{ marginRight: 6, color: '#845adf' }} />
                Configuración del Modelo
              </span>
            }
          >
            <Row gutter={[24, 16]} align="middle">
              {/* Modelo por defecto */}
              <Col xs={24} md={10}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>MODELO POR DEFECTO</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Select
                    style={{ flex: 1 }}
                    value={defaultModel}
                    onChange={async (value) => {
                      try {
                        await updateConfig({ default_model: value }).unwrap();
                        message.success(`Modelo por defecto cambiado a ${value}`);
                      } catch {
                        message.error('Error al actualizar el modelo');
                      }
                    }}
                    loading={configLoading || updatingConfig}
                    showSearch
                    placeholder="Seleccionar modelo"
                    optionFilterProp="label"
                    options={[
                      ...availableModels.map(m => ({
                        label: (
                          <span>
                            {runningModels.some(r => r.name === m.name) && <FireOutlined style={{ marginRight: 4, color: '#f5b849' }} />}
                            {m.name}
                            <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 8 }}>
                              {formatBytes(m.size)}{m.details?.parameter_size ? ` · ${m.details.parameter_size}` : ''}
                            </span>
                          </span>
                        ),
                        value: m.name,
                      })),
                      // Si el modelo configurado no está en la lista, agregarlo
                      ...(!availableModels.some(m => m.name === defaultModel)
                        ? [{ label: `${defaultModel} (no descargado)`, value: defaultModel }]
                        : []),
                    ]}
                  />
                  <Tooltip title="Cargar modelo en GPU">
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      loading={loadingModel}
                      onClick={async () => {
                        try {
                          await loadModel({ model: defaultModel }).unwrap();
                          message.success(`Modelo ${defaultModel} cargado en GPU`);
                        } catch {
                          message.error('Error al cargar el modelo');
                        }
                      }}
                      style={{ background: '#845adf', borderColor: '#845adf' }}
                    >
                      Cargar
                    </Button>
                  </Tooltip>
                </div>
                {configData?.config?.default_model?.updated_at && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    Actualizado: {configData.config.default_model.updated_at}
                  </div>
                )}
              </Col>

              <Col xs={0} md={1}>
                <Divider type="vertical" style={{ height: 60 }} />
              </Col>

              {/* Descargar nuevo modelo */}
              <Col xs={24} md={13}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>DESCARGAR NUEVO MODELO</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    placeholder="ej: qwen2.5-coder:14b, llama3:8b, mistral:latest"
                    value={pullModelName}
                    onChange={(e) => setPullModelName(e.target.value)}
                    onPressEnter={async () => {
                      if (!pullModelName.trim()) return;
                      try {
                        await pullModel({ model: pullModelName.trim() }).unwrap();
                        message.success(`Descarga de ${pullModelName} iniciada en Ollama`);
                        setPullModelName('');
                      } catch {
                        message.error('Error al iniciar descarga del modelo');
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    icon={<DownloadOutlined />}
                    loading={pullingModel}
                    onClick={async () => {
                      if (!pullModelName.trim()) {
                        message.warning('Ingrese el nombre del modelo');
                        return;
                      }
                      try {
                        await pullModel({ model: pullModelName.trim() }).unwrap();
                        message.success(`Descarga de ${pullModelName} iniciada en Ollama`);
                        setPullModelName('');
                      } catch {
                        message.error('Error al iniciar descarga del modelo');
                      }
                    }}
                    style={{ borderColor: '#845adf', color: '#845adf' }}
                  >
                    Descargar
                  </Button>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Ingrese el nombre del modelo del registro de Ollama para descargarlo al servidor
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      )}

      {/* Server Hardware Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {(() => {
          const cargaStr = serverInfo.carga || '';
          const loadAvgs = cargaStr.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
          const load1 = loadAvgs[0] ?? null;
          const cores = parseInt(serverInfo.nucleos) || 12;
          const loadPct = load1 != null ? Math.min(Math.round((load1 / cores) * 100), 100) : null;

          // CPU from history
          const lastCpu = cpuSeries.length > 0 ? cpuSeries[cpuSeries.length - 1] : null;
          const cpuPct = lastCpu ? Math.round(lastCpu.cpu) : loadPct;
          const cpuSparkData = cpuSeries.slice(-40).map(d => d.cpu);

          // RAM from history
          const lastRam = ramSeries.length > 0 ? ramSeries[ramSeries.length - 1] : null;
          const ramPct = lastRam ? Math.round(lastRam.porcentaje) : 0;
          const ramUsedGb = lastRam ? (lastRam.usado_mb / 1024).toFixed(1) : '—';
          const ramTotalGb = lastRam ? (lastRam.total_mb / 1024).toFixed(1) : (serverInfo.ram || '32');
          const ramSparkData = ramSeries.slice(-40).map(d => d.porcentaje);

          // Network from history
          const lastNet = netSeries.length > 0 ? netSeries[netSeries.length - 1] : null;
          const netRx = lastNet ? lastNet.rx : 0;
          const netTx = lastNet ? lastNet.tx : 0;
          const netConn = lastNet ? lastNet.conexiones : 0;
          const netSparkRx = netSeries.slice(-40).map(d => d.rx);
          const netSparkTx = netSeries.slice(-40).map(d => d.tx);

          // Disk from estadoservidores
          const parseDisk = (s) => { const m2 = (s || '').match(/([\d.]+)/); return m2 ? parseFloat(m2[1]) : 0; };
          const discoTotal = parseDisk(serverInfo.disco);
          const discoLibre = parseDisk(serverInfo.espaciolibre);
          const discoUsado = discoTotal - discoLibre;
          const discoPct = discoTotal > 0 ? Math.round((discoUsado / discoTotal) * 100) : 0;

          // Disk I/O from history
          const lastDiskIo = diskIoSeries.length > 0 ? diskIoSeries[diskIoSeries.length - 1] : null;
          const diskRead = lastDiskIo ? lastDiskIo.lectura : 0;
          const diskWrite = lastDiskIo ? lastDiskIo.escritura : 0;

          const colorForPct = (p) => p == null ? '#d9d9d9' : p < 50 ? '#26bf94' : p < 80 ? '#f5b849' : '#e6533c';

          const sparkOpts = (color, extra) => ({
            chart: { type: 'area', sparkline: { enabled: true }, animations: { enabled: false } },
            stroke: { curve: 'smooth', width: 1.5 },
            fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
            colors: [color],
            tooltip: { enabled: false },
            ...extra,
          });

          const radialOpts = (pct, color) => ({
            chart: { type: 'radialBar', sparkline: { enabled: true }, animations: { enabled: false } },
            plotOptions: {
              radialBar: {
                startAngle: -90, endAngle: 90,
                hollow: { size: '58%' },
                track: { background: '#e9edf4', strokeWidth: '100%' },
                dataLabels: {
                  name: { show: false },
                  value: { offsetY: -2, fontSize: '15px', fontWeight: 700, color: '#1e293b', formatter: () => `${pct}%` },
                },
              },
            },
            colors: [color],
          });

          const CARD_STYLE = { borderRadius: 12, overflow: 'hidden', height: '100%' };
          const BODY_STYLE = { padding: '14px 16px 6px', display: 'flex', flexDirection: 'column', height: '100%' };

          return (
            <>
              {/* CPU */}
              <Col xs={12} sm={12} lg={6}>
                <Card bordered={false} style={CARD_STYLE} bodyStyle={BODY_STYLE}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                      <DashboardOutlined style={{ marginRight: 4, color: colorForPct(cpuPct) }} />CPU
                    </span>
                    {cpuPct != null && (
                      <Tag color={cpuPct < 50 ? 'success' : cpuPct < 80 ? 'warning' : 'error'} style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>
                        {cpuPct}%
                      </Tag>
                    )}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
                    {cpuPct != null ? `${cpuPct}%` : '—'}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                    Load: {load1 != null ? load1.toFixed(2) : '—'} · {cores} núcleos
                  </div>
                  {cpuPct != null && (
                    <Progress percent={cpuPct} showInfo={false} strokeColor={colorForPct(cpuPct)} size="small" style={{ marginBottom: 4 }} />
                  )}
                  <div style={{ marginTop: 'auto' }}>
                    {cpuSparkData.length > 1 ? (
                      <Chart options={sparkOpts(colorForPct(cpuPct))} series={[{ data: cpuSparkData }]} type="area" height={50} />
                    ) : <div style={{ height: 50 }} />}
                  </div>
                </Card>
              </Col>

              {/* RAM */}
              <Col xs={12} sm={12} lg={6}>
                <Card bordered={false} style={CARD_STYLE} bodyStyle={BODY_STYLE}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                      <DatabaseOutlined style={{ marginRight: 4, color: colorForPct(ramPct) }} />RAM
                    </span>
                    {ramPct > 0 && (
                      <Tag color={ramPct < 70 ? 'success' : ramPct < 90 ? 'warning' : 'error'} style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>
                        {ramPct}%
                      </Tag>
                    )}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
                    {ramUsedGb} <span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8' }}>GB</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                    de {ramTotalGb} GB · {lastRam ? `${(lastRam.libre_mb / 1024).toFixed(1)} GB libres` : '—'}
                  </div>
                  <Progress percent={ramPct} showInfo={false} strokeColor={colorForPct(ramPct)} size="small" style={{ marginBottom: 2 }} />
                  <div style={{ marginTop: 'auto' }}>
                    {ramSparkData.length > 1 ? (
                      <Chart options={sparkOpts(colorForPct(ramPct))} series={[{ data: ramSparkData }]} type="area" height={50} />
                    ) : (
                      <Chart options={radialOpts(ramPct, colorForPct(ramPct))} series={[ramPct]} type="radialBar" height={100} />
                    )}
                  </div>
                </Card>
              </Col>

              {/* Disco */}
              <Col xs={12} sm={12} lg={6}>
                <Card bordered={false} style={CARD_STYLE} bodyStyle={BODY_STYLE}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                      <HddOutlined style={{ marginRight: 4, color: colorForPct(discoPct) }} />Disco
                    </span>
                    <Tag color={discoPct < 70 ? 'success' : discoPct < 90 ? 'warning' : 'error'} style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>
                      {discoPct}% usado
                    </Tag>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
                    {discoLibre > 0 ? `${discoLibre}G` : '—'} <span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8' }}>libres</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                    {discoUsado > 0 ? `${discoUsado}G usados` : ''} de {discoTotal > 0 ? `${discoTotal}G` : '—'}
                    {lastDiskIo ? ` · I/O: ${diskRead}/${diskWrite} KB/s` : ''}
                  </div>
                  <Progress percent={discoPct} showInfo={false} strokeColor={colorForPct(discoPct)} size="small" style={{ marginBottom: 2 }} />
                  <div style={{ marginTop: 'auto' }}>
                    <Chart options={radialOpts(discoPct, colorForPct(discoPct))} series={[discoPct]} type="radialBar" height={100} />
                  </div>
                </Card>
              </Col>

              {/* Red / Network */}
              <Col xs={12} sm={12} lg={6}>
                <Card bordered={false} style={CARD_STYLE} bodyStyle={BODY_STYLE}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                      <CloudServerOutlined style={{ marginRight: 4, color: '#845adf' }} />Red
                    </span>
                    {netConn > 0 && (
                      <Tag color="blue" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>
                        {netConn} conn
                      </Tag>
                    )}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
                    {netRx > 0 || netTx > 0 ? `${netRx.toFixed(2)}` : '—'} <span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8' }}>MB/s ↓</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                    ↑ {netTx.toFixed(2)} MB/s · {netConn} conexiones
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                    <Tag color="blue">{cores} núcleos</Tag>
                    <Tag color="purple">{serverInfo.ram || '32G'} RAM</Tag>
                    <Tag>{runningModels.length} modelo{runningModels.length !== 1 ? 's' : ''}</Tag>
                  </div>
                  <div style={{ marginTop: 'auto' }}>
                    {netSparkRx.length > 1 ? (
                      <Chart
                        options={sparkOpts('#845adf', { colors: ['#845adf', '#23b7e5'] })}
                        series={[{ name: 'RX', data: netSparkRx }, { name: 'TX', data: netSparkTx }]}
                        type="area"
                        height={50}
                      />
                    ) : <div style={{ height: 50 }} />}
                  </div>
                </Card>
              </Col>
            </>
          );
        })()}
      </Row>

      {/* Chat KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { title: 'Chats Hoy', value: parseInt(today.chats_today) || 0, icon: <MessageOutlined />, color: '#845adf', bg: '#f3eeff' },
          { title: 'Chats Mes', value: parseInt(month.chats_month) || 0, icon: <RiseOutlined />, color: '#23b7e5', bg: '#e6f7ff' },
          { title: 'Tokens In (Hoy)', value: formatTokens(parseInt(today.prompt_tokens_today) || 0), icon: <ApiOutlined />, color: '#26bf94', bg: '#e8fdf5' },
          { title: 'Tokens Out (Hoy)', value: formatTokens(parseInt(today.completion_tokens_today) || 0), icon: <ThunderboltOutlined />, color: '#f5b849', bg: '#fff8e6' },
          { title: 'Tokens In (Mes)', value: formatTokens(parseInt(month.prompt_tokens_month) || 0), icon: <ApiOutlined />, color: '#49b6f5', bg: '#e6f7ff' },
          { title: 'Tokens Out (Mes)', value: formatTokens(parseInt(month.completion_tokens_month) || 0), icon: <ThunderboltOutlined />, color: '#e6533c', bg: '#fff1f0' },
          { title: 'Prom. Duración Hoy', value: formatDuration(Math.round(parseFloat(today.avg_duration_today) || 0)), icon: <ClockCircleOutlined />, color: '#8c6bef', bg: '#f3eeff' },
          { title: 'Errores Hoy', value: parseInt(today.errors_today) || 0, icon: <CloseCircleOutlined />, color: parseInt(today.errors_today) > 0 ? '#e6533c' : '#26bf94', bg: parseInt(today.errors_today) > 0 ? '#fff1f0' : '#e8fdf5' },
        ].map((kpi, idx) => (
          <Col xs={12} sm={8} md={6} lg={3} key={idx}>
            <Card bordered={false} style={{ borderRadius: 12, textAlign: 'center', minHeight: 120 }} bodyStyle={{ padding: '16px 12px' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: kpi.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: kpi.color, marginBottom: 8 }}>
                {kpi.icon}
              </div>
              <Statistic
                title={<span style={{ fontSize: 11, color: '#94a3b8' }}>{kpi.title}</span>}
                value={kpi.value}
                valueStyle={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Hourly Chart */}
        <Col xs={24} lg={16}>
          <Card bordered={false} style={{ borderRadius: 12 }}
            title={<span style={{ fontSize: 14, fontWeight: 600 }}><MessageOutlined style={{ marginRight: 6, color: '#845adf' }} />Actividad por Hora (Hoy)</span>}
          >
            {statsLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
            ) : (
              <Chart options={hourlyChartOptions} series={hourlyChartSeries} type="area" height={280} />
            )}
          </Card>
        </Col>

        {/* Origin Pie */}
        <Col xs={24} lg={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}
            title={<span style={{ fontSize: 14, fontWeight: 600 }}><ApiOutlined style={{ marginRight: 6, color: '#23b7e5' }} />Distribución por Origen</span>}
            extra={
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="small"
                items={[
                  { key: 'today', label: 'Hoy' },
                  { key: 'month', label: 'Mes' },
                ]}
              />
            }
          >
            {statsLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
            ) : originPieSeries.length === 0 ? (
              <Empty description="Sin datos" />
            ) : (
              <Chart options={originPieOptions} series={originPieSeries} type="donut" height={280} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Daily 30d Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Card bordered={false} style={{ borderRadius: 12 }}
            title={<span style={{ fontSize: 14, fontWeight: 600 }}><RiseOutlined style={{ marginRight: 6, color: '#f5b849' }} />Actividad Últimos 30 Días</span>}
          >
            {statsLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
            ) : daily30d.length === 0 ? (
              <Empty description="Sin datos históricos" />
            ) : (
              <Chart options={dailyChartOptions} series={dailyChartSeries} type="line" height={260} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Tables Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* By Origin Table */}
        <Col xs={24} lg={12}>
          <Card bordered={false} style={{ borderRadius: 12 }}
            title={<span style={{ fontSize: 14, fontWeight: 600 }}>Chats por Origen — Hoy</span>}
          >
            <Table
              dataSource={byOriginToday}
              columns={originColumns}
              rowKey="origin"
              size="small"
              pagination={false}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card bordered={false} style={{ borderRadius: 12 }}
            title={<span style={{ fontSize: 14, fontWeight: 600 }}>Chats por Origen — Mes</span>}
          >
            <Table
              dataSource={byOriginMonth}
              columns={originColumns}
              rowKey="origin"
              size="small"
              pagination={false}
              loading={statsLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Top Models + Recent Chats */}
      <Row gutter={[16, 16]}>
        {/* Top Models */}
        <Col xs={24} lg={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}
            title={<span style={{ fontSize: 14, fontWeight: 600 }}><FireOutlined style={{ marginRight: 6, color: '#e6533c' }} />Modelos Más Usados (Mes)</span>}
          >
            {statsLoading ? (
              <Spin />
            ) : topModels.length === 0 ? (
              <Empty description="Sin datos" />
            ) : (
              <div>
                {topModels.map((m, i) => {
                  const maxChats = Math.max(...topModels.map(x => parseInt(x.chats) || 1));
                  const pct = Math.round(((parseInt(m.chats) || 0) / maxChats) * 100);
                  return (
                    <div key={i} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{m.model}</span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{parseInt(m.chats).toLocaleString()} chats · {formatTokens(parseInt(m.tokens))} tk</span>
                      </div>
                      <Progress
                        percent={pct}
                        showInfo={false}
                        strokeColor="#845adf"
                        size="small"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        {/* Recent Chats */}
        <Col xs={24} lg={16}>
          <Card bordered={false} style={{ borderRadius: 12 }}
            title={<span style={{ fontSize: 14, fontWeight: 600 }}><ClockCircleOutlined style={{ marginRight: 6, color: '#845adf' }} />Últimos Chats</span>}
          >
            <Table
              dataSource={recentChats}
              columns={recentColumns}
              rowKey="id"
              size="small"
              pagination={false}
              loading={statsLoading}
              scroll={{ x: 700 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
